import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { notification } from '../../../../components/common';
import {
  goldenPinModalOpenEvent,
  goldenPinPurchaseEvent,
  goldenPinPurchaseFailedEvent,
} from '../../../../services/analyticsService';
import { getItem, setItem } from '../../../../utility/localStorageControl';

export const getPendingCardKey = (userId) => `goldenPinPendingCard_${userId}`;

export const readPendingCards = (userId) => {
  try {
    const value = getItem(getPendingCardKey(userId));
    return value && typeof value === 'object' ? value : {};
  } catch (e) {
    return {};
  }
};

export const writePendingCards = (userId, value) => {
  try {
    setItem(getPendingCardKey(userId), value);
  } catch (e) {
    // Storage unavailable: the card purchase event is simply not recovered later.
  }
};

/**
 * Callbacks for useApplyProductModalData that own Golden Pin messaging + GA4 tracking.
 * Credit purchases fire `golden_pin_purchase` on apply success; card purchases are recorded as
 * pending here and fire once My Listings reports the pin applied (see useGoldenPinLifecycleEvents).
 */
export const useGoldenPinOptions = (user, isMobile) => {
  const { t } = useTranslation();
  const listingRef = useRef(null);

  return useMemo(
    () => ({
      suppressDefaultToasts: true,
      onModalOpen: (listing) => {
        listingRef.current = listing;
        goldenPinModalOpenEvent(user, listing, isMobile ? 'mweb' : 'web');
      },
      onApplySuccess: (response, action) => {
        notification.success(t('Golden pin active'));
        goldenPinPurchaseEvent(user, {
          listing: listingRef.current,
          price: action?.requiredCredits,
          durationDays: action?.expiryDays,
          paymentMethod: 'credits',
        });
      },
      onApplyError: (error) => {
        notification.error(error || t("Couldn't apply golden pin. Please try again."));
        goldenPinPurchaseFailedEvent(user, { listing: listingRef.current, failureReason: error });
      },
      onCartCreated: (response, action, { quantity, expiryDays }) => {
        const listing = listingRef.current;
        const listingId = listing?.id ?? listing?.property_id;
        if (!user?.id || !listingId) return;
        writePendingCards(user.id, {
          ...readPendingCards(user.id),
          [listingId]: {
            price: (Number(quantity) || 0) * (Number(action?.credit_unit_price) || 0),
            duration_days: expiryDays,
            purpose: listing?.purpose,
            city: listing?.location?.title,
            location: listing?.location?.breadcrumb,
            area: listing?.area,
            ts: Date.now(),
          },
        });
      },
      onCartError: (error) => {
        notification.error(error || t("Couldn't apply golden pin. Please try again."));
        goldenPinPurchaseFailedEvent(user, { listing: listingRef.current, failureReason: 'cart_failed' });
      },
    }),
    [user, isMobile, t],
  );
};
